import React, { useState, useEffect, useRef } from 'react';
import { X, Save, SlidersHorizontal, AlertCircle, Loader } from 'lucide-react';
import { NumericInput } from '../../../common/inputs';
import { useTranslation } from '../../../../contexts/I18nProvider';

/** Et leveringsmåte-valg i nedtrekket — description er allerede ryddet (strippet for " | "-notater). */
export interface BulkDeliveryMethodOption {
  deliveryMethodNo: number;
  description: string;
}

/**
 * Rydder en DeliveryMethods.Description for visning: fjerner interne arbeidsnotater fra " | "
 * og utover (samme transform som backend bruker i Leveringsmåte-kolonnen). Brukes når nedtrekket
 * lastes via deliveryMethodService.getCurrent() (som returnerer rå Description).
 */
export const cleanDeliveryMethodDescription = (desc: string | null | undefined): string => {
  if (!desc) return '';
  const idx = desc.indexOf(' | ');
  return (idx >= 0 ? desc.slice(0, idx) : desc).trim();
};

/** Endringene brukeren har valgt. Kun avkryssede felt er med. */
export interface BulkUpdateChanges {
  priority?: number;
  deliveryDate?: string | null; // ISO yyyy-MM-dd
  deliveryMethodNo?: number;
}

interface BulkUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Antall plukklister som er valgt. */
  selectedCount: number;
  /** Lagring pågår (parent eier bulk-lagringen). */
  isSaving: boolean;
  /** Valgfri framdrift (f.eks. 3 av 10) vist under lagring. */
  progress?: { done: number; total: number } | null;
  /** Tilgjengelige leveringsmåter (ryddet beskrivelse) for nedtrekket. */
  deliveryMethods: BulkDeliveryMethodOption[];
  /** Kalles når brukeren bekrefter. Inneholder kun avkryssede felt. */
  onConfirm: (changes: BulkUpdateChanges) => void;
}

/**
 * BulkUpdateModal — massehandling for valgte plukklister. Lar lagersjef oppdatere
 * ett eller flere felt samtidig via avkryssing-per-felt (mønster: ZoneBulkUpdateModal):
 *   ☐ Prioritet      → PList.Priority
 *   ☐ Leveringsdato  → PList.DeliveryDate
 *   ☐ Leveringsmåte  → PListOrders.DeliveryMethodNo (alle ordrer på plukket)
 *
 * Kun avkryssede felt sendes i onConfirm. Tema-kompatibel (CSS-variabler via Tailwind),
 * NumericInput for nettbrett-vennlig tallinntasting.
 */
export const BulkUpdateModal: React.FC<BulkUpdateModalProps> = ({
  isOpen,
  onClose,
  selectedCount,
  isSaving,
  progress,
  deliveryMethods,
  onConfirm
}) => {
  const { t } = useTranslation();

  const [enablePriority, setEnablePriority] = useState(false);
  const [enableDeliveryDate, setEnableDeliveryDate] = useState(false);
  const [enableDeliveryMethod, setEnableDeliveryMethod] = useState(false);

  const [priority, setPriority] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryMethodNo, setDeliveryMethodNo] = useState('');

  const [error, setError] = useState<string | null>(null);
  const priorityRef = useRef<HTMLInputElement>(null);

  // Nullstill all state hver gang modalen åpnes
  useEffect(() => {
    if (isOpen) {
      setEnablePriority(false);
      setEnableDeliveryDate(false);
      setEnableDeliveryMethod(false);
      setPriority('');
      setDeliveryDate('');
      setDeliveryMethodNo('');
      setError(null);
    }
  }, [isOpen]);

  // Fokuser prioritetsfeltet når det krysses av
  useEffect(() => {
    if (enablePriority) {
      const id = setTimeout(() => priorityRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [enablePriority]);

  if (!isOpen) return null;

  const enabledCount = (enablePriority ? 1 : 0) + (enableDeliveryDate ? 1 : 0) + (enableDeliveryMethod ? 1 : 0);

  const selectedLabel = selectedCount === 1
    ? t('manager.modules.newPickingLists.bulkUpdate.listSingular', {}, 'plukkliste')
    : t('manager.modules.newPickingLists.bulkUpdate.listPlural', {}, 'plukklister');

  const handleConfirm = () => {
    if (isSaving) return;

    if (enabledCount === 0) {
      setError(t('manager.modules.newPickingLists.bulkUpdate.errorNoField', {}, 'Velg minst ett felt å oppdatere'));
      return;
    }

    const changes: BulkUpdateChanges = {};

    if (enablePriority) {
      const trimmed = priority.trim();
      if (trimmed === '') {
        setError(t('manager.modules.newPickingLists.bulkUpdate.errorPriorityRequired', {}, 'Skriv inn en prioritet'));
        return;
      }
      const value = parseInt(trimmed, 10);
      if (Number.isNaN(value) || value < 0 || value > 999) {
        setError(t('manager.modules.newPickingLists.bulkUpdate.errorPriorityRange', {}, 'Prioritet må være et tall mellom 0 og 999'));
        return;
      }
      changes.priority = value;
    }

    if (enableDeliveryDate) {
      if (deliveryDate.trim() === '') {
        setError(t('manager.modules.newPickingLists.bulkUpdate.errorDeliveryDateRequired', {}, 'Velg en leveringsdato'));
        return;
      }
      changes.deliveryDate = deliveryDate;
    }

    if (enableDeliveryMethod) {
      if (deliveryMethodNo === '') {
        setError(t('manager.modules.newPickingLists.bulkUpdate.errorDeliveryMethodRequired', {}, 'Velg en leveringsmåte'));
        return;
      }
      changes.deliveryMethodNo = parseInt(deliveryMethodNo, 10);
    }

    setError(null);
    onConfirm(changes);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50 transition-opacity" onClick={isSaving ? undefined : onClose} />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl">
            <div className="flex items-center gap-3">
              <SlidersHorizontal className="w-5 h-5 text-white" />
              <div>
                <h2 className="text-lg font-semibold text-white">
                  {t('manager.modules.newPickingLists.bulkUpdate.title', {}, 'Oppdater valgte')}
                </h2>
                <p className="text-sm text-blue-100">
                  {t('manager.modules.newPickingLists.bulkUpdate.selectedSummary', { '0': selectedCount, '1': selectedLabel }, `${selectedCount} ${selectedLabel} valgt`)}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={isSaving}
              className="text-white/80 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4 overflow-y-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800">
                  {t('manager.modules.newPickingLists.bulkUpdate.info', {}, 'Velg feltene du ønsker å oppdatere. Kun avkryssede felt endres for alle valgte plukklister.')}
                </p>
              </div>
            </div>

            {/* Prioritet */}
            <div className={`border rounded-lg p-4 transition-colors ${enablePriority ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="bulk-enable-priority"
                  checked={enablePriority}
                  onChange={(e) => { setEnablePriority(e.target.checked); if (error) setError(null); }}
                  disabled={isSaving}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="bulk-enable-priority" className="text-sm font-medium text-gray-700 cursor-pointer">
                  {t('manager.modules.newPickingLists.bulkUpdate.priorityLabel', {}, 'Prioritet')}
                </label>
              </div>
              {enablePriority && (
                <div className="mt-2">
                  <NumericInput
                    ref={priorityRef}
                    id="bulk-priority-input"
                    mode="integer"
                    value={priority}
                    onChange={(e) => { setPriority(e.target.value); if (error) setError(null); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleConfirm(); } }}
                    disabled={isSaving}
                    placeholder={t('manager.modules.newPickingLists.bulkUpdate.priorityPlaceholder', {}, 'F.eks. 10 (lavere = høyere prioritet)')}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
              )}
            </div>

            {/* Leveringsdato */}
            <div className={`border rounded-lg p-4 transition-colors ${enableDeliveryDate ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="bulk-enable-deliverydate"
                  checked={enableDeliveryDate}
                  onChange={(e) => { setEnableDeliveryDate(e.target.checked); if (error) setError(null); }}
                  disabled={isSaving}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="bulk-enable-deliverydate" className="text-sm font-medium text-gray-700 cursor-pointer">
                  {t('manager.modules.newPickingLists.bulkUpdate.deliveryDateLabel', {}, 'Leveringsdato')}
                </label>
              </div>
              {enableDeliveryDate && (
                <div className="mt-2">
                  <input
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => { setDeliveryDate(e.target.value); if (error) setError(null); }}
                    disabled={isSaving}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 bg-white"
                  />
                </div>
              )}
            </div>

            {/* Leveringsmåte */}
            <div className={`border rounded-lg p-4 transition-colors ${enableDeliveryMethod ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'}`}>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="bulk-enable-deliverymethod"
                  checked={enableDeliveryMethod}
                  onChange={(e) => { setEnableDeliveryMethod(e.target.checked); if (error) setError(null); }}
                  disabled={isSaving}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="bulk-enable-deliverymethod" className="text-sm font-medium text-gray-700 cursor-pointer">
                  {t('manager.modules.newPickingLists.bulkUpdate.deliveryMethodLabel', {}, 'Leveringsmåte')}
                </label>
              </div>
              {enableDeliveryMethod && (
                <div className="mt-2 space-y-2">
                  <select
                    value={deliveryMethodNo}
                    onChange={(e) => { setDeliveryMethodNo(e.target.value); if (error) setError(null); }}
                    disabled={isSaving}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100"
                  >
                    <option value="">{t('manager.modules.newPickingLists.bulkUpdate.deliveryMethodSelect', {}, '-- Velg leveringsmåte --')}</option>
                    {deliveryMethods.map(dm => (
                      <option key={dm.deliveryMethodNo} value={dm.deliveryMethodNo}>
                        {dm.description ? `${dm.deliveryMethodNo} – ${dm.description}` : dm.deliveryMethodNo}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    {t('manager.modules.newPickingLists.bulkUpdate.deliveryMethodHint', {}, 'Settes på alle ordrer på de valgte plukklistene. Påvirker både visning og fraktbestilling (TA) i plukk-flyt.')}
                  </p>
                </div>
              )}
            </div>

            {error && (
              <p className="text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" /> {error}
              </p>
            )}

            {isSaving && progress && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <Loader className="w-4 h-4 animate-spin text-blue-600" />
                  <span>
                    {t('manager.modules.newPickingLists.bulkUpdate.savingProgress', { '0': progress.done, '1': progress.total }, `Oppdaterer ${progress.done} av ${progress.total}...`)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.buttons.cancel', {}, 'Avbryt')}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isSaving || enabledCount === 0}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              {isSaving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {isSaving
                ? t('common.status.saving', {}, 'Lagrer...')
                : t('manager.modules.newPickingLists.bulkUpdate.confirmButton', {}, 'Bekreft')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUpdateModal;
